import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  confirmFertSlotOrder,
  ConfirmFertSlotOrderBody,
  reassignFertSlotOrder,
  getFertSlotRequestDetail,
  getFertSlotRequestOrdersDetail,
  updateFertSlotRequest,
} from "@/services/fert-slot-requests.service";
import { getStatusByEntity } from "@/services/entity-status.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { FieldValues } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { EstadosSolicitudesInsumos } from "@/constants/enums/status/entity-status-ids";

export interface BaseHandlerProps {
  id: string | undefined;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}
export const handleCreateOrderHandler = (
  navigate: NavigateFunction,
  id: string | undefined,
) => {
  if (!id) return;
  navigate(AppRoutes.FERT_SLOTS_REQ_ORDER_CREATION.replace(":id", String(id)));
};

export const reassignOrderHandler = async (params: {
  orderId: number;
  body: ConfirmFertSlotOrderBody;
  id: string | undefined;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingConfirmOrderId: (value: number | null) => void;
  fetchOrders: (showLoad?: boolean) => Promise<void>;
}) => {
  const {
    orderId,
    body,
    id,
    navigate,
    fetchPermissions,
    setLoadingConfirmOrderId,
    fetchOrders,
  } = params;

  setLoadingConfirmOrderId(orderId);
  try {
    await reassignFertSlotOrder(orderId, body);

    toaster.create({
      type: "success",
      title: "Pedido reasignado exitosamente.",
    });

    await fetchOrders(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudo reasignar el pedido.",
        ),
      });
    }
  } finally {
    setLoadingConfirmOrderId(null);
  }
};

export const handleEditOrdersHandler = (
  navigate: NavigateFunction,
  id: string | undefined,
) => {
  if (!id) return;
  navigate(AppRoutes.FERT_SLOTS_REQ_ORDER_DETAIL.replace(":id", String(id)));
};

export const getStatusIdByNameHandler = (
  name: string,
  statusOptions: { nombre: string; ids: number[] }[],
  fallbackId?: number,
) => {
  const found = statusOptions.find((s) => s.nombre === name)?.ids?.[0];
  return typeof found === "number" ? found : fallbackId;
};

export const fetchStatusOptionsHandler = async (params: {
  setStatusOptions: (value: { nombre: string; ids: number[] }[]) => void;
}) => {
  const { setStatusOptions } = params;
  try {
    const response = await getStatusByEntity({ Id: 12 });
    setStatusOptions(response ?? []);
  } catch {
    setStatusOptions([]);
  }
};

export const fetchDetailHandler = async (params: {
  id: string | undefined;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingDetail: (value: boolean) => void;
  setDetail: (value: any) => void;
  showLoad?: boolean;
}) => {
  const {
    id,
    navigate,
    fetchPermissions,
    setLoadingDetail,
    setDetail,
    showLoad = true,
  } = params;

  try {
    if (showLoad) setLoadingDetail(true);
    const response = await getFertSlotRequestDetail({ id: Number(id) });
    setDetail(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudo cargar la solicitud.",
        ),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoadingDetail(false);
  }
};

export const fetchOrdersHandler = async (params: {
  id: string | undefined;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingOrders: (value: boolean) => void;
  setOrders: (value: any) => void;
  showLoad?: boolean;
}) => {
  const {
    id,
    navigate,
    fetchPermissions,
    setLoadingOrders,
    setOrders,
    showLoad = true,
  } = params;

  try {
    if (showLoad) setLoadingOrders(true);
    const response = await getFertSlotRequestOrdersDetail(Number(id));
    setOrders(response ?? []);
  } catch (error) {
    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudieron cargar los pedidos.",
        ),
      });
    }
    throw error;
  } finally {
    if (showLoad) setLoadingOrders(false);
  }
};

export interface SendInfoHandlerProps extends BaseHandlerProps {
  event?: FieldValues;
  setLoadingSendInfo: (value: boolean) => void;
  setOpenInfoModal: (value: boolean) => void;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}

export const sendInfoHandler = async ({
  id,
  event,
  navigate,
  fetchPermissions,
  setLoadingSendInfo,
  setOpenInfoModal,
  fetchDetail,
}: SendInfoHandlerProps) => {
  setLoadingSendInfo(true);
  try {
   

    await updateFertSlotRequest(Number(id), {
      idEstado: EstadosSolicitudesInsumos.RequiereInformacionSolicitudInsumos,
      detalle: event?.request_detail,
    });

    toaster.create({
      type: "success",
      title: "Pedido de información realizado exitosamente",
    });

    await fetchDetail(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "Hubo un error al enviar el pedido de información.",
        ),
      });
    }
  } finally {
    setLoadingSendInfo(false);
    setOpenInfoModal(false);
  }
};

export interface RejectHandlerProps extends BaseHandlerProps {
  event?: FieldValues;
  setLoadingReject: (value: boolean) => void;
  setOpenRejectModal: (value: boolean) => void;
  fetchDetail: (showLoad?: boolean) => Promise<void>;
}

export const rejectHandler = async ({
  id,
  event,
  navigate,
  fetchPermissions,
  setLoadingReject,
  setOpenRejectModal,
  fetchDetail,
}: RejectHandlerProps) => {
  setLoadingReject(true);
  try {
   

    await updateFertSlotRequest(Number(id), {
      idEstado: EstadosSolicitudesInsumos.RechazadaInsumos,
      detalle: event?.request_detail,
    });

    toaster.create({
      type: "success",
      title: "Solicitud rechazada exitosamente.",
    });

    await fetchDetail(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error as AxiosError, "No se pudo rechazar la solicitud."),
      });
    }
  } finally {
    setLoadingReject(false);
    setOpenRejectModal(false);
  }
};

export interface ConfirmOrderHandlerProps {
  orderId: number;
  body: ConfirmFertSlotOrderBody;
  id: string | undefined;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingConfirmOrderId: (value: number | null) => void;
  fetchOrders: (showLoad?: boolean) => Promise<void>;
}

export const confirmOrderHandler = async ({
  orderId,
  body,
  id,
  navigate,
  fetchPermissions,
  setLoadingConfirmOrderId,
  fetchOrders,
}: ConfirmOrderHandlerProps) => {
  setLoadingConfirmOrderId(orderId);
  try {
    await confirmFertSlotOrder(orderId, body);

    toaster.create({
      type: "success",
      title: "Pedido asignado exitosamente.",
    });

    await fetchOrders(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error as AxiosError, "No se pudo asignar el pedido."),
      });
    }
  } finally {
    setLoadingConfirmOrderId(null);
  }
};
